<template>
  <div class="dpb" role="toolbar" :aria-label="$tn('dashboardWidgets.presets.title')">
    <div class="dpb__scroll">
      <div
        v-for="p in presets"
        :key="p._id"
        class="dpb__chip"
        :class="{ 'is-active': p._id === activeId }"
      >
        <button
          type="button"
          class="dpb__use"
          :title="titleOf(p)"
          :aria-pressed="p._id === activeId ? 'true' : 'false'"
          @click="$emit('select', p)"
        >
          <i v-if="p.isDefault" class="fas fa-star dpb__mark" aria-hidden="true"></i>
          <i v-else-if="!p.mine" class="fas fa-users dpb__mark" aria-hidden="true"></i>
          <span class="dpb__name">{{ p.name }}</span>
          <span v-if="p._id === activeId && dirty" class="dpb__dirty" :title="$tn('dashboardWidgets.presets.unsaved')"></span>
        </button>
        <!-- Cambios sin guardar en el que está en uso: guardarlos de un click. -->
        <button
          v-if="p._id === activeId && dirty"
          type="button"
          class="dpb__edit dpb__save"
          :title="saveTitle(p)"
          :aria-label="saveTitle(p)"
          @click="$emit('save-changes', p)"
        >
          <i class="fas fa-save" aria-hidden="true"></i>
          <span class="dpb__save-label">{{ $tn("dashboardWidgets.presets.saveShort") }}</span>
        </button>
        <button
          v-if="p.canEdit"
          type="button"
          class="dpb__edit"
          :title="$tn('dashboardWidgets.presets.editOne', { name: p.name })"
          :aria-label="$tn('dashboardWidgets.presets.editOne', { name: p.name })"
          @click="$emit('edit', p)"
        >
          <i class="fas fa-pen" aria-hidden="true"></i>
        </button>
      </div>
      <button
        type="button"
        class="dpb__new"
        data-tour="dw-preset-new"
        :title="$tn('dashboardWidgets.presets.saveCurrent')"
        :aria-label="$tn('dashboardWidgets.presets.saveCurrent')"
        @click="$emit('create')"
      >
        <i class="fas fa-plus" aria-hidden="true"></i>
        <span v-if="!presets.length" class="dpb__new-label">{{ $tn("dashboardWidgets.presets.saveCurrentShort") }}</span>
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Un botón por dashboard guardado (propios y compartidos): click = usarlo; lápiz =
 * editar ese. El «+» guarda el dashboard actual como uno nuevo.
 */
export default {
  name: "DashboardPresetBar",
  props: {
    presets: { type: Array, default: () => [] },
    activeId: { type: String, default: null },
    dirty: { type: Boolean, default: false },
  },
  methods: {
    saveTitle(p) {
      return p.canEdit
        ? this.$tn("dashboardWidgets.presets.saveChangesIn", { name: p.name })
        : this.$tn("dashboardWidgets.presets.saveAsNew");
    },
    titleOf(p) {
      const parts = [p.name];
      if (p.description) parts.push(p.description);
      if (!p.mine && p.createdBy && p.createdBy.name) parts.push(this.$tn("dashboardWidgets.presets.by", { name: p.createdBy.name }));
      return parts.join(" — ");
    },
  },
};
</script>

<style scoped>
.dpb { flex: 1; min-width: 0; }
.dpb__scroll { display: flex; align-items: center; gap: 6px; overflow-x: auto; scrollbar-width: thin; padding: 2px 0; }
.dpb__chip {
  display: inline-flex;
  align-items: stretch;
  flex-shrink: 0;
  border: 1px solid var(--dw-border);
  border-radius: 999px;
  background: var(--dw-surface);
  overflow: hidden;
}
.dpb__chip.is-active { border-color: var(--bs-primary); background: rgba(var(--bs-primary-rgb), 0.12); }
.dpb__use {
  border: 0;
  background: transparent;
  color: var(--dw-text);
  font-size: 0.8rem;
  padding: 4px 10px 4px 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  max-width: 220px;
}
.dpb__chip.is-active .dpb__use { color: var(--bs-primary); font-weight: 600; }
.dpb__name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dpb__mark { font-size: 0.68rem; color: var(--dw-muted); }
.dpb__chip.is-active .dpb__mark { color: var(--bs-primary); }
.dpb__dirty { width: 7px; height: 7px; border-radius: 50%; background: var(--bs-warning); flex-shrink: 0; }
.dpb__edit {
  border: 0;
  border-left: 1px solid var(--dw-border);
  background: transparent;
  color: var(--dw-muted);
  padding: 0 9px;
  font-size: 0.7rem;
  cursor: pointer;
}
.dpb__edit:hover { color: var(--dw-text); background: var(--dw-surface-2); }
.dpb__save { color: var(--bs-primary); display: inline-flex; align-items: center; gap: 5px; font-size: 0.72rem; font-weight: 600; }
.dpb__save:hover { color: var(--bs-primary); background: rgba(var(--bs-primary-rgb), 0.12); }
.dpb__new {
  flex-shrink: 0;
  border: 1px dashed var(--dw-border);
  background: transparent;
  color: var(--dw-muted);
  border-radius: 999px;
  height: 28px;
  min-width: 28px;
  padding: 0 9px;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
}
.dpb__new:hover { color: var(--bs-primary); border-color: var(--bs-primary); }
</style>
